import { AfterViewInit, Component, ElementRef, inject, Renderer2, ViewChild, DestroyRef, OnInit } from '@angular/core';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  AvatarComponent,
  BorderDirective,
  ButtonCloseDirective,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  ListGroupDirective,
  ListGroupItemDirective,
  ProgressComponent,
  SidebarComponent,
  SidebarHeaderComponent,
  SidebarToggleDirective,
  TabDirective,
  TabPanelComponent,
  TabsComponent,
  TabsContentComponent,
  TabsListComponent
} from '@coreui/angular-pro';
import { AppState } from '../../../store/app.state';
import * as NoteActions from '../../../store/main/actions/note.actions';
import { selectNotes, selectNoteFilters } from '../../../store/main/selectors/note.selectors';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../../authentication/authentication.service';
import { Note, NoteFilters } from '../../../store/main/models/note.model';
import * as UiActions from '../../../store/ui/actions/ui.actions';
import { MessageType } from '../../../store/payment/models/payroll.model';
import { AutoFocusDirective } from 'src/app/views/components/auto-focus-directive/auto-focus.directive';

@Component({
  selector: 'app-notes-aside',
  templateUrl: './notes-aside.component.html',
  standalone: true,
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    RouterLink,
    FormsModule,
    ButtonCloseDirective,
    SidebarToggleDirective,
    NgTemplateOutlet,
    DatePipe,
    AutoFocusDirective,
    ListGroupDirective,
    ListGroupItemDirective,
    BorderDirective,
    AvatarComponent,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    ProgressComponent,
    TabsComponent,
    TabsListComponent,
    TabDirective,
    TabsContentComponent,
    TabPanelComponent
  ]
})
export class NotesAsideComponent implements OnInit, AfterViewInit {
  private organizationId!: string;
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  @ViewChild('aside1', { static: false }) aside1?: any;

  // public messages = Array.from({ length: 5 }, (v, i) => i); //TODO: remove after you build UI. Is like a mock for now
  // public notes: Note[] = [];
  public activeNotes: Partial<Note>[] = [];
  public closedNotes: Partial<Note>[] = [];
  public currentFilters?: NoteFilters;

  private loading = {
    saveNote: false,
  }


  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private renderer: Renderer2
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.organizationId = await this.authService.getOrganizationId()
    this.store.select(selectNoteFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filters => {
        this.currentFilters = filters;
      });

    // Load notes only once
    this.loadNotes();
  }

  loadNotes() {
    this.store.select(selectNotes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notes => {
        const notesCopy = structuredClone(notes); // Create a copy as the original object is immutable
        this.activeNotes = notesCopy.filter(note => note.endDate === null);
        this.closedNotes = notesCopy.filter(note => note.endDate !== null);
        this.loading.saveNote = false;
      });
  }

  onNoteCloseChange(note: Note, event: any): void {
    console.log('onNoteCloseChange', note.id);
    const isChecked = event.target.checked;

    // Don't allow closing notes without ID (draft notes)
    if (!note?.id) {
      return;
    }

    if (!isChecked) {
      // Switch turned OFF - close the note
      console.log('Closing note - dispatching closeNote action for:', note.id);
      this.store.dispatch(NoteActions.closeNote({
        noteId: note.id,
        organizationId: this.organizationId!
      }));
    } else {
      // Switch turned ON - activate the note again
      console.log('Activating note - updating note with endDate: null');

      const activatedNote = {
        ...note,
        endDate: null
      };

      this.store.dispatch(NoteActions.updateNote({
        organizationId: this.organizationId!,
        noteId: note.id,
        note: activatedNote
      }));
    }
  }

  addNewNote(): void {
    const firstNote = this.activeNotes?.[0];
    // Check if there's already a draft note (note without ID)
    if (firstNote && !firstNote.id) {
      // Don't allow adding if there's already a draft note
      return;
    }
    const newNote: Partial<Note> = {
      group: 'Payment',
      isEditing: true
    };

    this.activeNotes.unshift(newNote);
  }

  startEdit(note: Note): void {
    note.isEditing = true;
  }

  saveNote(note: Note): void {
    if (!note) {
      return;
    }
    // Check if this is a new note (no ID) or existing note
    if (!note.id) {
      // Set loading flag
      this.loading.saveNote = true;

      this.store.dispatch(NoteActions.createNote({
        organizationId: this.organizationId!,
        note: note
      }));
    } else {
      // This is an existing note - call update API
      console.log('Updating existing note:', note.id, 'with description:', note.description);

      // Call the API - the effect will handle updateNoteSuccess
      this.store.dispatch(NoteActions.updateNote({
        organizationId: this.organizationId!,
        noteId: note.id!,
        note: note
      }));
    }
  }

  cancelEdit(note: Note): void {
    if (!note.id) {
      this.activeNotes.splice(0, 1);
    } else {
      note.isEditing = false;
    }
  }

  ngAfterViewInit(): void {
    this.renderer.removeStyle(this.elementRef.nativeElement, 'display');
  }
}
